import React, { useContext, useEffect, useState } from 'react'
import googleLogo from './google-logo.svg'
import './styles.scss'
import Axios from '../../utilities/axios'
import Spinner from '../spinner'
import { Context } from '../../contexts/GlobalContext'
import { useSearchParams } from 'react-router-dom'

const ContinueWithGoogle = () => {
  const url = process.env.NODE_ENV === 'development' ? 'http://localhost:4000/api/user/login-google' : 'https://sellagen.com/api/user/login-google'
  return (
    <a className='sv2-continue-google' href={url}>
      <img src={googleLogo} alt='Google-logo'/>Continue with Google
    </a>
  )
}

const AccessInputBox = ({ value='', onChange = () => {}, type='text', label='', autoFocus=false, onKeyDown = () => {} }) => {
  return (
    <div className='sv2-access-input-container'>
      <input
        className='sv2-access-input'
        value={value}
        onChange={onChange}
        type={type}
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
      />
      <label
        style={ {
          fontFamily: 'Roboto Mono, monospace',
          position: 'absolute', top: '8px', left: '12px', fontSize: '11px'
        } }
      >{label}</label>
    </div>
  )
}

const errorMappings = {
  'urlgenerationerror': 'Unable to generate the Google authentication URL. Please try again later.',
  'csrfattackerror': 'Security verification failed. Please restart the login process.',
  'missingemailfield': 'Google login failed: No email address was found in your Google account. Please ensure your account has a verified email address.',
  'invalidemailerror': 'Invalid email format received from Google. Please try again with a different email address.',
  'emailinuseerror': 'An account with this email already exists using a different sign-in method. Please log in with your email and password.',
  'duplicateemailerror': 'Account creation conflict detected. Please try logging in instead.',
  'accountcreationerror': 'Unable to create your account at this time. Please try again or contact support if the issue persists.',
  'authtokenerror': 'Login verification was completed, but we encountered an issue finalizing your session. Please try logging in again.',
  'default': 'Unable to create your account at this time. Please try again or contact support if the issue persists.'
}

const AccountAccessPanel = ( { panelMode = 'login', isCloseDisabled = false }) => {
  const [searchParams] = useSearchParams()
  // for auto panel switching
  const [panel, setPanel] = useState(panelMode || 'login')

  // all form fields
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  // intermediate states
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)
  const [isShowingFullForm,  setIsShowingFullForm] = useState(false)
  const [errorMessage, setErrorMessage] = useState()

  // switching states
  const [isSwitchedFromLogin, setIsSwitchedFromLogin] = useState(false)

  const { globalState, setGlobalState, toggleAccountAccess } = useContext(Context)

  useEffect(() => {
    const errorCode = searchParams.get('error') || null

    if (errorCode) {
      const message = errorMappings[String(errorCode).toLowerCase() || ''] || errorMappings['default']
      setErrorMessage(message)
    } else {
      setErrorMessage(null)
    }
  }, [searchParams])

  if(globalState?.user?._id) return null

  const switchToPanel = (mode='login') => {
    setIsSubmitLoading(false)
    setErrorMessage('')
    setIsShowingFullForm(false)
    setPassword('')
    setFirstName('')
    setLastName('')
    if (mode === 'create-new' && panel === 'login') {
      setIsSwitchedFromLogin(true)
    }
    if (['login', 'create-new', 'reset-password'].includes(mode)) {
      setPanel(mode)
    } else {
      setPanel('create-new')
    }
  }

  // note: used with signup
  const emailLookupHandler = async () => {
    // exit function when laready loading email query or already revealed full form
    if (isShowingFullForm) return
    if (isSubmitLoading) return

    setIsSubmitLoading(true)
    setErrorMessage('')

    try {
      const endpoint = 'user/lookup'
      const {success, data=null, error=null} = (await Axios.post(endpoint, { email })).data

      if (data?.accessMethod === 'login-google' && data?.accessUrl) {
        window.location.assign(data?.accessUrl)
        // note: redirect
      } else if (data?.accessMethod === 'login-default') {
        setIsShowingFullForm(false)
        setPanel('login')
      } else if (data?.accessMethod === 'create-new') {
        setIsShowingFullForm(true)
        setPanel('create-new')
      } else {
        setIsShowingFullForm(false)
        setPanel('create-new')
        setErrorMessage('There was an error. Try again later.')
      }
    } catch (error) {
      // parse error
      setErrorMessage('[Catch]: There was an error. Try again later.')
    } finally {
      setIsSubmitLoading(false)
    }
  }

  const createNewAccountHandler = async () => {
    // api call for new account creation
    if (isSubmitLoading) return
    setIsSubmitLoading(true)
    setErrorMessage('')
    
    const payload = { email, password, firstName, lastName }
    try {
      const { user } = (await Axios.post('user/new-v2', payload, { withCredentials: false })).data
      if (user) {
        setGlobalState({user}, () => {
          localStorage.setItem('user', JSON.stringify(user))
          window.location.href = '/nelima'
        })
      } else {
        setErrorMessage('There was an error while creating new account. Please try again later.')
      }
      setIsSubmitLoading(false)
    } catch (error) {
      if (error.response?.data?.error === 'ValidationError') {
        if (error.response?.data?.message?.length > 0) {
          let errorMessages = error.response?.data?.message || []
          const errorView = (
            <>
              <span><strong>Error Creating Account</strong></span>
              <div style={{fontSize: '12px'}}>
                {errorMessages.map((msg, index) => (
                  <div style={{display: 'flex'}}>
                    •<div style={{marginLeft: '5px'}} key={index}>{msg}</div>
                  </div>
                ))}
              </div>
            </>
          )
          setErrorMessage(errorView)
        } else {
          setErrorMessage('There was an unknown validation error. Please try again.')
        }
      } else {
        setErrorMessage('There was an error while creating new account. Please try again.')
      }
      setIsSubmitLoading(false)
    }
  }

  const loginHandler = async () => {
    if (isSubmitLoading) return
    setIsSubmitLoading(true)
    setErrorMessage('')
    
    const payload = { email, password, willRememberMe: true }
    try {
      const { user } = (await Axios.post('user/login', payload, { withCredentials: false })).data
      if (user) {
        setGlobalState({user}, () => {
          localStorage.setItem('user', JSON.stringify(user))
          window.location.href = '/nelima'
        })
      } else {
        setErrorMessage('There was an error logging in. Please try again later.')
      }
      setIsSubmitLoading(false)
    } catch (error) {
      if(error?.response?.status === 404) {
        setErrorMessage('That email and password combination is incorrect.')
      } else {
        setErrorMessage('There was an error logging in. Please try again.')
      }
      setIsSubmitLoading(false)
    }
  }

  let submitButtonContent = null
  let fullForm = null

  if (panel === 'reset-password') {
    return (<ResetPasswordPanel isCloseDisabled={isCloseDisabled} switchToPanel={switchToPanel} />)
  } else if (panel === 'create-new') {
    let submitHandler = emailLookupHandler
    let submitMsg = 'Continue with email'
    if (isShowingFullForm) {
      fullForm = (
        <>
          <AccessInputBox 
            value={password} 
            onChange={ e => {
              setPassword(e.target.value)
            } }
            label='PASSWORD'
            type='password'
            autoFocus={true}
          />
          <div style={{margin: '30px 0 0 0'}}></div>
          <AccessInputBox 
            value={firstName} 
            onChange={ e => {
              setFirstName(e.target.value)
            } }
            label='FIRST NAME'
            type='text'
          />
          <div style={{margin: '30px 0 0 0'}}></div>
          <AccessInputBox 
            value={lastName} 
            onChange={ e => {
              setLastName(e.target.value)
            } }
            label='LAST NAME'
            type='text'
          />
          <div style={{margin: '30px 0 0 0'}}></div>
        </>
      )
      submitHandler = createNewAccountHandler
      submitMsg = 'Create Account'
    }
    submitButtonContent = (
      <div className='sv2-signin-status' style={ {padding: '15px', fontWeight: 500, borderColor: 'rgba(230, 230, 230, 0.5)', borderRadius: '10px', fontSize: '17px'} }
        onClick={ submitHandler }
      >
        { isSubmitLoading ? <Spinner dark/> : submitMsg } 
      </div>
    )
  } else {
    // login mode
    fullForm = (
      <>
        <AccessInputBox 
          value={password} 
          onChange={ e => {
            setPassword(e.target.value)
          } }
          label='PASSWORD'
          type='password'
        />
        <div style={{margin: '30px 0 0 0'}}></div>
      </>
    )
    submitButtonContent = (
      <div className='sv2-signin-status' style={ {padding: '15px', fontWeight: 500, borderColor: 'rgba(230, 230, 230, 0.5)', borderRadius: '10px', fontSize: '17px'} }
        onClick={loginHandler}
      >
        { isSubmitLoading ? <Spinner dark/> : 'Log in' }
      </div>
    )
  }

  let accountModeToggle = null
  if (panel === 'login' && !isSwitchedFromLogin) {
    accountModeToggle = (
      <>
        <div style={{margin: '35px 0 0 0', fontSize: '13px', letterSpacing: '0.25px'}}>
          <span style={{cursor: 'pointer', color: 'cornflowerblue' }}
            onClick={ () => { switchToPanel('reset-password') } }
          >Reset Password</span>
        </div>
        <div style={{margin: '25px 0 0 0', fontSize: '12px', letterSpacing: '0.25px'}}>
          No Account? <span style={{cursor: 'pointer', color: 'cornflowerblue' }}
            onClick={ () => { switchToPanel('create-new') } }
          >Create One</span>
        </div>
      </>
    )
  } else if (panel === 'create-new' && isShowingFullForm) {
    accountModeToggle = (
      <div style={{margin: '30px 0 0 0', fontSize: '12px', letterSpacing: '0.25px'}}>
        Already have an account? <span style={{cursor: 'pointer', color: 'cornflowerblue' }}
          onClick={ () => { switchToPanel('login') } }
        >Log in</span>
      </div>
    )
  }
  
  return (
    <div className='sv2-signin-main'>
      <div className="sv2-signin-auth-container" style={{ textAlign: 'center', padding: '45px 40px 40px 40px'}}>
        { !(isCloseDisabled === true) && 
          <div className='sv2-signin-close' onClick={toggleAccountAccess}><i className="fal fa-times"></i></div>
        }
        <ContinueWithGoogle/>
        <div style={{margin: '20px 0'}}>or</div>
        <AccessInputBox 
          value={email} 
          onChange={ e => {
            setEmail(e.target.value)
          } }
          autoFocus={true}
          label='EMAIL'
          type='text'
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (panel === 'create-new' && !isShowingFullForm ) {
                emailLookupHandler()
              }
            }
          }}
        />
        <div style={{margin: '30px 0 0 0'}}></div>
        {fullForm}
        {submitButtonContent}
        {
          errorMessage ? 
          <>
            <div style={{margin: '30px 0 0 0'}}></div>
            <div className='sv2-access-error'>
              <div style={{marginLeft: '5px', borderLeft: '2px solid rgba(220, 20, 60, 0.85)', paddingLeft: '15px'}}>
                {errorMessage}
              </div>
            </div>
          </>
          : null
        }
        {accountModeToggle}
      </div>
    </div>
  )
}

const ResetPasswordPanel = ({ switchToPanel = null, isCloseDisabled = false }) => {
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [token, setToken] = useState('')
  const [isInvalid, setIsInvalid] = useState(false)
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)
  const [showResetSend, setShowResetSend] = useState(false)
  const [isPassResetSuccess, setIsPassResetSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { toggleAccountAccess } = useContext(Context)

  const submitHandler = async () => {
    if (isSubmitLoading) return null
    if (!email) return null

    setIsSubmitLoading(true)
    try {
      // call api
      const { success } = (await Axios.get(`user/account/get-password-reset/${encodeURIComponent(email)}`)).data
      if (success) {
        setShowResetSend(true)
      } else {
        setErrorMessage('There was an error while resetting the password. Please try again later.')
      }
    } catch (error) {
      setShowResetSend(false)
      setErrorMessage('There was an error while resetting the password. Please try again later.')
    } finally {
      setIsSubmitLoading(false)
    }
  }

  const submitPasswordChange = async () => {
    if (isSubmitLoading) return null
    if (!password) return null

    setIsSubmitLoading(true)
    try {
      // call api
      const { success } = (await Axios.post(`user/account/reset-password`, {token, password})).data
      if (success) {
        setIsPassResetSuccess(true)
        setErrorMessage(null)
      } else {
        setIsPassResetSuccess(false)
        setErrorMessage('There was an error while resetting the password. Please try again later.')
      }
    } catch (error) {
      if (error.response?.data?.error === 'ValidationError') {
        if (error.response?.data?.message?.length > 0) {
          let errorMessages = error.response?.data?.message || []
          const errorView = (
            <>
              <span><strong>Error Resetting Password</strong></span>
              <div style={{fontSize: '12px'}}>
                {errorMessages.map((msg, index) => (
                  <div key={index} style={{display: 'flex'}}>
                    •<div style={{marginLeft: '5px'}} key={index}>{msg}</div>
                  </div>
                ))}
              </div>
            </>
          )
          setErrorMessage(errorView)
        } else {
          setErrorMessage('There was an unknown validation error. Please try again.')
        }
      } else if (error.response?.data?.error === 'InvalidTokenError') {
        setErrorMessage('Password reset session expired. Request a new password reset link and continue.')
      } else {
        setErrorMessage('There was an error while resetting the password. Please try again later.')
      }
      setIsPassResetSuccess(false)
    } finally {
      setIsSubmitLoading(false)
    }
  }

  useEffect(() => {
    const errorCode = searchParams.get('error') || null
    if (errorCode) {
      if (errorCode === 'invalid') {
        setIsInvalid(true)
      } else {
        setErrorMessage('Unknow error occured. Try again later.')
      }
      return null
    }

    const tokenParam = searchParams.get('token') || null
    if (tokenParam) {
      setToken(decodeURIComponent(tokenParam))
    }
  }, [searchParams])

  if (isPassResetSuccess) {
    return (
      <div className='sv2-signin-main'>
        <div className="sv2-signin-auth-container" style={{ textAlign: 'center', padding: '45px 40px 40px 40px'}}>
          <div style={{textAlign: 'center'}}>
            <i style={{fontSize: '60px', color: 'rgb(255, 165, 2)'}} className="fal fa-key-skeleton"></i>
          </div>
          <div style={{margin: '30px 0', fontSize: '18px', fontWeight: 600}}>Password Reset Successful</div>
          <div className={`sv2-signin-status`} style={ {padding: '15px', fontWeight: 500, borderColor: 'rgba(230, 230, 230, 0.5)', borderRadius: '10px', fontSize: '17px'} }
            onClick={ () => {
              window.location.href = '/login'
            } }
          >
            Log In
          </div>
        </div>
      </div>
    )
  }

  if (isInvalid) {
    return (
      <div className='sv2-signin-main'>
        <div className="sv2-signin-auth-container" style={{ textAlign: 'center', padding: '45px 40px 40px 40px'}}>
          <div style={{textAlign: 'center'}}>
            <i style={{fontSize: '60px', color: 'rgb(255, 165, 2)'}} className="fal fa-exclamation-triangle"></i>
          </div>
          <div style={{margin: '30px 0', fontSize: '18px', fontWeight: 600}}>Unable to reset password</div>
          <div className='sv2-access-error' style={{backgroundColor: 'rgba(255, 165, 2, 0.03)', borderColor: 'rgba(255, 165, 2, 0.4)'}}>
            <div style={{marginLeft: '5px', borderLeft: '2px solid rgba(255, 165, 2, 0.85)', paddingLeft: '15px'}}>
              The reset link is invalid or expired. Try requesting a new one.
            </div>
          </div>
          <div style={{margin: '30px 0 0 0', fontSize: '13px', letterSpacing: '0.25px', fontWeight: 500}}>
            <span style={{cursor: 'pointer', color: 'cornflowerblue' }}
              onClick={ () => {
                window.location.href = '/login'
              } }
            >Back to Log in</span>
          </div>
        </div>
      </div>
    )
  }

  if (token) {
    return (
      <div className='sv2-signin-main'>
        <div className="sv2-signin-auth-container" style={{ textAlign: 'center', padding: '45px 40px 40px 40px'}}>
          <div style={{textAlign: 'center'}}>
            <i style={{fontSize: '60px', color: 'rgb(255, 165, 2)'}} className="fal fa-key-skeleton"></i>
          </div>
          <div style={{margin: '30px 0', fontSize: '18px', fontWeight: 600}}>Enter new password</div>
          <AccessInputBox 
            value={password} 
            onChange={ e => {
              setPassword(e.target.value)
            } }
            autoFocus={true}
            label='PASSWORD'
            type='password'
          />
          <div style={{margin: '30px 0 0 0'}}></div>
          <div className={`sv2-signin-status ${password ? '' : 'disable'}`} style={ {padding: '15px', fontWeight: 500, borderColor: 'rgba(230, 230, 230, 0.5)', borderRadius: '10px', fontSize: '17px'} }
            onClick={ submitPasswordChange }
          >
            { isSubmitLoading ? <Spinner dark/> : 'Set New Password' }
          </div>
          <div style={{margin: '30px 0 0 0', fontSize: '13px', letterSpacing: '0.25px', fontWeight: 500}}>
            <span style={{cursor: 'pointer', color: 'cornflowerblue' }}
              onClick={ () => {
                window.location.href = '/login'
              } }
            >Back to Log in</span>
          </div>
          {
            errorMessage ? 
            <>
              <div style={{margin: '30px 0 0 0'}}></div>
              <div className='sv2-access-error'>
                <div style={{marginLeft: '5px', borderLeft: '2px solid rgba(220, 20, 60, 0.85)', paddingLeft: '15px'}}>
                  {errorMessage}
                </div>
              </div>
            </>
            : null
          }
        </div>
      </div>
    )
  }

  if (showResetSend) {
    return (
      <div className='sv2-signin-main'>
        <div className="sv2-signin-auth-container" style={{ textAlign: 'center', padding: '45px 40px 40px 40px'}}>
          { !(isCloseDisabled === true) && 
            <div className='sv2-signin-close' onClick={toggleAccountAccess}><i className="fal fa-times"></i></div>
          }
          <div className='sv2-access-error' style={{backgroundColor: 'rgba(29, 191, 113, 0.03)', borderColor: 'rgba(29, 191, 113, 0.4)'}}>
            <div style={{marginLeft: '5px', borderLeft: '2px solid rgba(29, 191, 113, 0.85)', paddingLeft: '15px'}}>
              If an account exists for {email}, you will get an email with instructions on resetting your password. If it doesn't arrive, be sure to check your spam folder.
            </div>
          </div>
          {
            typeof switchToPanel === 'function' && 
            <div style={{margin: '30px 0 0 0', fontSize: '13px', letterSpacing: '0.25px', fontWeight: 500}}>
              <span style={{cursor: 'pointer', color: 'cornflowerblue' }}
                onClick={ () => { switchToPanel('login') } }
              >Back to Log in</span>
            </div>
          }
        </div>
      </div>
    )
  }
  
  return (
    <div className='sv2-signin-main'>
      <div className="sv2-signin-auth-container" style={{ textAlign: 'center', padding: '45px 40px 40px 40px'}}>
        { !(isCloseDisabled === true) && 
          <div className='sv2-signin-close' onClick={toggleAccountAccess}><i className="fal fa-times"></i></div>
        }
        <div style={{marginBottom: '30px', fontSize: '18px', fontWeight: 600}}>Enter your email to reset password</div>
        <AccessInputBox 
          value={email} 
          onChange={ e => {
            setEmail(e.target.value)
          } }
          autoFocus={true}
          label='EMAIL'
          type='text'
        />
        <div style={{margin: '30px 0 0 0'}}></div>
        <div className={`sv2-signin-status ${email ? '' : 'disable'}`} style={ {padding: '15px', fontWeight: 500, borderColor: 'rgba(230, 230, 230, 0.5)', borderRadius: '10px', fontSize: '17px'} }
          onClick={ submitHandler }
        >
          { isSubmitLoading ? <Spinner dark/> : 'Reset Password' } 
        </div>
        {
          errorMessage ? 
          <>
            <div style={{margin: '30px 0 0 0'}}></div>
            <div className='sv2-access-error'>
              <div style={{marginLeft: '5px', borderLeft: '2px solid rgba(220, 20, 60, 0.85)', paddingLeft: '15px'}}>
                {errorMessage}
              </div>
            </div>
          </>
          : null
        }
        {
          typeof switchToPanel === 'function' && 
          <div style={{margin: '30px 0 0 0', fontSize: '13px', letterSpacing: '0.25px', fontWeight: 500}}>
            <span style={{cursor: 'pointer', color: 'cornflowerblue' }}
              onClick={ () => { switchToPanel('login') } }
            >Cancel</span>
          </div>
        }
      </div>
    </div>
  )
}


export default AccountAccessPanel
export {ResetPasswordPanel}